import type { PerformanceStatus } from './performance.js';

export interface MobileBenchmarkRequest {
  npcCount: 5 | 11;
  durationMs: number;
  mapId: string;
  timeOfDay: 'night';
  weather: 'rain';
}

export interface MobileBenchmarkResult {
  version: 2;
  scenario: MobileBenchmarkRequest;
  userAgent: string;
  elapsedMs: number;
  samples: number;
  targetFps: number;
  medianFps: number;
  frameP50Ms: number;
  frameP95Ms: number;
  frameP99Ms: number;
  simulationP95Ms: number | null;
  gpuP95Ms: number | null;
  snapshotP95Ms: number | null;
  maxDrawCalls: number | null;
  maxTriangles: number | null;
  longTaskCount: number;
  longTaskTotalMs: number;
  longTaskMaxMs: number;
  heapStartMb: number | null;
  heapEndMb: number | null;
  heapPeakMb: number | null;
  heapPeakGrowthMb: number | null;
  heapDeltaMb: number | null;
  thermalFallbackSamples: number;
  thermalFallbackTransitions: number;
  budgetAlerts: number;
  passed: boolean;
}

declare global {
  interface Window {
    __nightshiftBenchmark?: MobileBenchmarkResult;
  }
}

export function parseMobileBenchmarkRequest(search: string): MobileBenchmarkRequest | null {
  const params = new URLSearchParams(search);
  if (params.get('benchmark') !== '1') return null;
  const durationSeconds = Number(params.get('duration') || 20);
  return {
    npcCount: params.get('npcs') === '5' ? 5 : 11,
    durationMs: Math.round(Math.min(120, Math.max(10, Number.isFinite(durationSeconds) ? durationSeconds : 20)) * 1_000),
    mapId: params.get('map') || 'alpine-midnight',
    timeOfDay: 'night',
    weather: 'rain',
  };
}

/** Aggregates the governor's fixed windows without creating per-frame garbage. */
export class MobileBenchmarkRecorder {
  private readonly statuses: PerformanceStatus[] = [];
  private readonly heapSamples: number[] = [];
  private observedAt: number | null = null;
  private startedAt: number | null = null;
  private finishedResult: MobileBenchmarkResult | null = null;
  private previousThermalFallback = false;
  private thermalTransitions = 0;

  constructor(
    readonly request: MobileBenchmarkRequest,
    private readonly userAgent: string,
    private readonly warmupMs = 3_000,
  ) {}

  record(status: PerformanceStatus, now: number, heapBytes: number | null): MobileBenchmarkResult | null {
    if (this.finishedResult) return this.finishedResult;
    this.observedAt ??= now;
    if (this.startedAt === null && now - this.observedAt < this.warmupMs) return null;
    this.startedAt ??= now;
    this.statuses.push({ ...status, budget: { ...status.budget } });
    if (heapBytes !== null && Number.isFinite(heapBytes)) this.heapSamples.push(heapBytes);
    if (status.thermalFallback && !this.previousThermalFallback) this.thermalTransitions += 1;
    this.previousThermalFallback = status.thermalFallback;
    if (now - this.startedAt < this.request.durationMs) return null;
    this.finishedResult = this.finish(now);
    return this.finishedResult;
  }

  progress(now: number): number {
    if (this.finishedResult) return 1;
    if (this.startedAt === null) return 0;
    return Math.min(1, Math.max(0, (now - this.startedAt) / this.request.durationMs));
  }

  private finish(now: number): MobileBenchmarkResult {
    const statuses = this.statuses;
    const targetFps = Math.round(median(statuses.map((status) => status.targetFps)));
    const longTaskTotalMs = statuses.reduce((total, status) => total + (status.longTaskTotalMs ?? 0), 0);
    const frameP95Ms = percentile(statuses.map((status) => status.frameP95Ms), 0.95);
    const medianFps = median(statuses.map((status) => status.fps));
    const elapsedMs = Math.max(0, now - (this.startedAt ?? now));
    const heapStart = this.heapSamples[0] ?? null;
    const heapEnd = this.heapSamples[this.heapSamples.length - 1] ?? null;
    const heapPeak = this.heapSamples.length > 0 ? Math.max(...this.heapSamples) : null;
    const frameBudget = benchmarkFrameBudgetMs(targetFps);
    const frameP99Ms = percentile(statuses.map((status) => status.frameP99Ms), 0.95);
    const longTaskMaxMs = nullableMax(statuses.map((status) => status.longTaskMaxMs ?? null)) ?? 0;
    const budgetAlerts = statuses.filter((status) => status.budgetExceeded).length;
    const allowedBudgetAlerts = Math.max(1, Math.floor(statuses.length * 0.1));
    const passed = medianFps >= targetFps * 0.9
      && frameP95Ms <= frameBudget
      && frameP99Ms <= frameBudget * 2.4
      && longTaskTotalMs <= Math.max(300, elapsedMs * 0.05)
      && longTaskMaxMs <= Math.max(70, frameBudget * 2)
      && budgetAlerts <= allowedBudgetAlerts;
    return {
      version: 2,
      scenario: this.request,
      userAgent: this.userAgent,
      elapsedMs,
      samples: statuses.length,
      targetFps,
      medianFps,
      frameP50Ms: median(statuses.map((status) => status.frameP50Ms)),
      frameP95Ms,
      frameP99Ms,
      simulationP95Ms: nullablePercentile(statuses.map((status) => status.cpuMs), 0.95),
      gpuP95Ms: nullablePercentile(statuses.map((status) => status.gpuMs), 0.95),
      snapshotP95Ms: nullablePercentile(statuses.map((status) => status.snapshotP95Ms ?? null), 0.95),
      maxDrawCalls: nullableMax(statuses.map((status) => status.drawCalls ?? null)),
      maxTriangles: nullableMax(statuses.map((status) => status.triangles ?? null)),
      longTaskCount: statuses.reduce((total, status) => total + (status.longTaskCount ?? 0), 0),
      longTaskTotalMs,
      longTaskMaxMs,
      heapStartMb: bytesToMb(heapStart),
      heapEndMb: bytesToMb(heapEnd),
      heapPeakMb: bytesToMb(heapPeak),
      heapPeakGrowthMb: heapStart === null || heapPeak === null ? null : bytesToMb(heapPeak - heapStart),
      heapDeltaMb: heapStart === null || heapEnd === null ? null : bytesToMb(heapEnd - heapStart),
      thermalFallbackSamples: statuses.filter((status) => status.thermalFallback).length,
      thermalFallbackTransitions: this.thermalTransitions,
      budgetAlerts,
      passed,
    };
  }
}

/** Smoothness budget for the only supported cadence tiers: 60, 30 and 20. */
export function benchmarkFrameBudgetMs(targetFps: number): number {
  if (targetFps >= 55) return 25;
  if (targetFps >= 29) return 42;
  return 58;
}

export function benchmarkHeapBytes(): number | null {
  const memory = (performance as Performance & { memory?: { usedJSHeapSize?: number } }).memory;
  const value = Number(memory?.usedJSHeapSize);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function nullablePercentile(values: Array<number | null>, quantile: number): number | null {
  const finite = values.filter((value): value is number => value !== null && Number.isFinite(value));
  return finite.length > 0 ? percentile(finite, quantile) : null;
}

function nullableMax(values: Array<number | null>): number | null {
  const finite = values.filter((value): value is number => value !== null && Number.isFinite(value));
  return finite.length > 0 ? Math.max(...finite) : null;
}

function median(values: number[]): number {
  return percentile(values, 0.5);
}

function percentile(values: number[], quantile: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((first, second) => first - second);
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(sorted.length * quantile) - 1));
  return sorted[index] ?? 0;
}

function bytesToMb(value: number | null): number | null {
  return value === null ? null : Math.round((value / (1_024 * 1_024)) * 100) / 100;
}
